import { useCallback, useMemo, useState } from 'react';
import { DeviceStatus } from '@weave/schema-gen-ts/dist/shared/phone/v1/presence/message.pb';
import { createContext, useContextSelector } from 'use-context-selector';
import { SoftphoneTypes } from '@frontend/api-softphone';
import { TeamPresenceV1 } from '@frontend/api-team-presence';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useSoftphoneEventSubscription } from './softphone-events-provider';

type UserPresence = 'vacant' | 'occupied';
type UserPresenceMap = { [uri: string]: UserPresence };
export type UserWithPresence = SoftphoneTypes.User & { status: UserPresence };

type SoftphoneUsersContextValue = {
  users: SoftphoneTypes.User[];
  getUser: (uri: string) => SoftphoneTypes.User | UserWithPresence | undefined;
  getExtension: (extensionNumber: string) => SoftphoneTypes.User | undefined;
  usersWithPresence: (UserWithPresence | SoftphoneTypes.User)[];
  setUserPresence: (uri: string, presence: UserPresence) => void;
  clearPresence: () => void;
};
const SoftphoneUsersContext = createContext(undefined as unknown as SoftphoneUsersContextValue);

type Props = {
  users: SoftphoneTypes.User[];
  otherExtensions: SoftphoneTypes.User[];
  children: React.ReactNode;
};
export const SoftphoneUsersProvider = ({ otherExtensions, users, children }: Props) => {
  const [usersPresenceMap, setUsersPresenceMap] = useState<UserPresenceMap | undefined>({});
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasPresenceFlag = getFeatureFlagValue('softphone-presence');

  const usersSipProfileIds = useMemo(() => {
    return users.map((user) => user.sipProfileId).filter((id): id is string => !!id);
  }, [users]);

  const fetchGetPresenceStatus = TeamPresenceV1.Queries.useFetchGetPresenceStatus();

  useSoftphoneEventSubscription('dialer-input.focused', () => {
    if (!hasPresenceFlag) return;
    fetchGetPresenceStatus({
      request: {
        sipProfileIds: usersSipProfileIds ?? [],
      },
      options: {
        staleTime: 0,
      },
    }).then((response) => {
      const res =
        response.devicePresence?.reduce((acc, presence) => {
          const user = users.find((u) => u.sipProfileId === presence.devicePresence?.sipProfileId);
          if (user) {
            const isAvailable = presence.devicePresence?.deviceStatus === DeviceStatus.DEVICE_STATUS_AVAILABLE;
            acc[user.id] = isAvailable ? 'vacant' : 'occupied';
          }

          return acc;
        }, {} as UserPresenceMap) ?? {};

      setUsersPresenceMap(res);
    });
  });

  const formattedUsers = useMemo(
    () =>
      users.map<(typeof users)[number]>((user) => {
        const extension = user.presenceUri?.split('@')[0].split('_')[1]; // sip_[extension]_id@domain
        const name = extension && !user.name.includes(extension) ? `${extension} ${user.name}` : user.name;
        return { ...user, name };
      }),
    [users]
  );

  const usersWithPresence = useMemo(() => {
    return formattedUsers.map<SoftphoneTypes.User | UserWithPresence>((user) => {
      return usersPresenceMap?.[user.id] ? { ...user, status: usersPresenceMap[user.id] } : user;
    });
  }, [formattedUsers, usersPresenceMap]);

  const getUser: SoftphoneUsersContextValue['getUser'] = useCallback(
    (uri) => {
      const formattedURI = uri.replace('sip:', '').split('@')[0];
      return usersWithPresence.find((user) => user.presenceUri?.split('@')[0] === formattedURI);
    },
    [usersWithPresence]
  );

  const getExtension: SoftphoneUsersContextValue['getUser'] = useCallback(
    (number) => {
      return (
        otherExtensions.find((extension) => extension.number === +number) ||
        formattedUsers.find((user) => user.number === +number)
      );
    },
    [otherExtensions, formattedUsers]
  );

  const value = {
    users: formattedUsers,
    usersWithPresence,
    getUser,
    getExtension,
    clearPresence: () => setUsersPresenceMap({}),
    setUserPresence: (uri: string, presence: UserPresence) =>
      setUsersPresenceMap((prev) => ({ ...prev, [uri]: presence })),
  } satisfies SoftphoneUsersContextValue;

  return <SoftphoneUsersContext.Provider value={value}>{children}</SoftphoneUsersContext.Provider>;
};

export const useSoftphoneUsers = <T extends any>(selector: (value: SoftphoneUsersContextValue) => T) => {
  return useContextSelector(SoftphoneUsersContext, selector);
};

export const isUserWithPresence = (user: SoftphoneTypes.User | UserWithPresence): user is UserWithPresence =>
  'status' in user;
